import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FormFieldComponent } from '../../../../shared/components/form-field/form-field.component';
import { FormTextareaComponent } from '../../../../shared/components/form-textarea/form-textarea.component';
import { BlocoEmail, ROTULO_BLOCO, TipoBlocoEmail, VariavelEmail } from '../../../../shared/model/email/modelo-email.model';
import { MsgService } from '../../../../shared/service/msg.service';

type Alinhamento = 'left' | 'center' | 'right';

@Component({
  selector: 'app-bloco-email-editor',
  standalone: true,
  imports: [CommonModule, MatIconModule, FormFieldComponent, FormTextareaComponent],
  templateUrl: './bloco-email-editor.component.html',
  styleUrl: './bloco-email-editor.component.css'
})
export class BlocoEmailEditorComponent {
  private readonly msgService = inject(MsgService);
  @Input() bloco!: BlocoEmail;
  @Input() primeiro = false;
  @Input() ultimo = false;
  @Input() disabled = false;
  @Input() variaveis: VariavelEmail[] = [];

  @Output() mudou = new EventEmitter<void>();
  @Output() subir = new EventEmitter<void>();
  @Output() descer = new EventEmitter<void>();
  @Output() moverTopo = new EventEmitter<void>();
  @Output() moverFundo = new EventEmitter<void>();
  @Output() excluir = new EventEmitter<void>();

  @ViewChild('imagemInput') imagemInput!: ElementRef<HTMLInputElement>;
  @ViewChild('tituloInput') tituloInput?: FormFieldComponent;
  @ViewChild('linkBotaoInput') linkBotaoInput?: FormFieldComponent;
  @ViewChild('textoTextarea') textoTextarea?: FormTextareaComponent;
  @ViewChild('htmlTextarea') htmlTextarea?: FormTextareaComponent;

  readonly coresBotao = ['#61B460', '#2B2B2B', '#6213B6'];
  readonly alinhamentos: Alinhamento[] = ['left', 'center', 'right'];

  private readonly icones: Record<TipoBlocoEmail, string> = {
    TITULO: 'title',
    TEXTO: 'notes',
    IMAGEM: 'image',
    BOTAO: 'smart_button',
    VARIAVEL: 'data_object',
    DIVISOR: 'horizontal_rule',
    HTML: 'code'
  };
  private readonly iconesAlinhamento: Record<Alinhamento, string> = {
    left: 'format_align_left',
    center: 'format_align_center',
    right: 'format_align_right'
  };
  private readonly rotulosAlinhamento: Record<Alinhamento, string> = {
    left: 'à esquerda',
    center: 'ao centro',
    right: 'à direita'
  };

  get icone(): string {
    return this.icones[this.bloco.tipo];
  }
  get rotulo(): string {
    return ROTULO_BLOCO[this.bloco.tipo];
  }
  iconeAlinhamento(a: Alinhamento): string {
    return this.iconesAlinhamento[a];
  }
  rotuloAlinhamento(a: Alinhamento): string {
    return this.rotulosAlinhamento[a];
  }

  get exibeAlinhamentoRecolhido(): boolean {
    return this.bloco.tipo !== 'DIVISOR';
  }

  get recolhido(): boolean {
    return !!this.bloco.recolhido;
  }

  alternarRecolhido(): void {
    this.bloco.recolhido = !this.recolhido;
    if (!this.recolhido) {
      setTimeout(() => {
        this.textoTextarea?.recalcularAltura();
        this.htmlTextarea?.recalcularAltura();
      }, 0);
    }
  }

  atualizarTexto(valor: string): void {
    this.bloco.texto = valor;
    this.mudou.emit();
  }
  atualizarCampo(campo: keyof BlocoEmail, valor: string): void {
    (this.bloco as unknown as Record<string, unknown>)[campo] = valor;
    this.mudou.emit();
  }

  inserirVariavelTitulo(token: string): void {
    this.inserirVariavelEmCampo('texto', token, this.tituloInput);
  }

  inserirVariavelLinkBotao(token: string): void {
    this.inserirVariavelEmCampo('urlBotao', token, this.linkBotaoInput);
  }

  private inserirVariavelEmCampo(
    campo: 'texto' | 'urlBotao',
    token: string,
    componente?: FormFieldComponent,
  ): void {
    const input = componente?.internalInput?.nativeElement;
    const atual = this.bloco[campo] ?? '';
    const inicio = input?.selectionStart ?? atual.length;
    const fim = input?.selectionEnd ?? inicio;
    const novaPosicao = inicio + token.length;

    this.bloco[campo] = `${atual.slice(0, inicio)}${token}${atual.slice(fim)}`;
    if (input) {
      input.value = this.bloco[campo] ?? '';
    }
    this.mudou.emit();

    setTimeout(() => {
      input?.focus({ preventScroll: true });
      input?.setSelectionRange(novaPosicao, novaPosicao);
    }, 0);
  }

  inserirVariavelTexto(token: string): void {
    this.textoTextarea?.inserirNoCursor(token);
  }

  inserirVariavelHtml(token: string): void {
    this.htmlTextarea?.inserirNoCursor(token);
  }

  definirAlinhamento(a: Alinhamento): void {
    this.bloco.alinhamento = a;
    this.mudou.emit();
  }
  definirCor(cor: string): void {
    this.bloco.corBotao = cor;
    this.mudou.emit();
  }
  definirLargura(evento: Event): void {
    this.bloco.largura = Number((evento.target as HTMLInputElement).value);
    this.mudou.emit();
  }

  abrirSeletorImagem(): void {
    this.imagemInput.nativeElement.click();
  }
  anexarImagem(evento: Event): void {
    const input = evento.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(file.type)) {
      this.msgService.msgAdvertencia('Selecione uma imagem PNG, JPG, GIF ou WebP.');
      input.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.msgService.msgAdvertencia('A imagem deve ter no máximo 10 MB.');
      input.value = '';
      return;
    }
    input.value = '';
    const leitor = new FileReader();
    leitor.onload = () => {
      this.bloco.urlImagem = leitor.result as string;
      this.mudou.emit();
    };
    leitor.onerror = () => this.msgService.msgErro('Não foi possível carregar a imagem selecionada.');
    leitor.readAsDataURL(file);
  }

  get urlImagemExibida(): string {
    return this.bloco.urlImagem ?? '';
  }

  get corAtualEhPrimaria(): boolean {
    return (this.bloco.corBotao ?? '#993DF5') === '#993DF5';
  }

  get corAtualEhPersonalizada(): boolean {
    const atual = this.bloco.corBotao ?? '#993DF5';
    return atual !== '#993DF5' && !this.coresBotao.includes(atual);
  }

  get corParaInputPicker(): string {
    return this.corAtualEhPersonalizada ? (this.bloco.corBotao as string) : '#993DF5';
  }

  definirCorPersonalizada(evento: Event): void {
    const valor = (evento.target as HTMLInputElement).value;
    this.definirCor(valor);
  }

}
